import React from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import DashboardLayout from "./layouts/dashboard";
import SchoolPortalLayout from "./layouts/schoolPortal";
import InstructorOnboardingLayout from "./layouts/instructorOnboarding";
import LogoOnlyLayout from "./layouts/LogoOnlyLayout";
import RequireModule from "./components/RequireModule";
import RequireRole from "./components/RequireRole";
import {
  Dashboard,
  PassengersList,
  Login,
  AdminActivation,
  ForgotPassword,
  ResetPassword,
  InstructorSignup,
  BecomeInstructor,
  Marketing,
  Payments,
  Feedback,
  FlightSchoolsList,
  AddOrUpdateFlightSchool,
  FlightSchoolDetail,
  FlightSchoolAdminDashboard,
  Console,
  ConsolePerson,
  ConsoleSchool,
  ApplicationsList,
  ApplicationDetail,
  JobPostingsList,
  AddOrUpdateJobPosting,
} from "./pages";
import {
  SchoolDashboard,
  SchoolStudents,
  SchoolStudentDetail,
  SchoolStaff,
  SchoolFeedback,
  SchoolBookMeeting,
  MyTeaching,
  SessionPage,
  VideoTest,
  SessionRoom,
  BookingsRevenue,
  SchoolSettings,
  ConnectOnboardingReturn,
  InstructorOnboarding,
} from "./pages/SchoolPortal";

export default function Router() {
  const Authentication = () => {
    if (localStorage.getItem("token")) {
      const userData = JSON.parse(localStorage.getItem("user_data") || "{}");
      if (userData.type === "school_admin" || userData.flightSchool) {
        // Everyone lands on the dashboard now. It is the work queue — what
        // needs you today — not an analytics page, so there is no module that
        // should route someone away from it.
        return <Navigate to="/school/dashboard" />;
      }
      return <Navigate to="/flight-schools" />;
    }
    return <Navigate to="/login" />;
  };

  return (
    <Routes>
      {/* XAir Admin */}
      <Route element={<DashboardLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/members"   element={<PassengersList />} />
        <Route path="/marketing" element={<Marketing />} />
        <Route path="/payments"  element={<Payments />} />
        <Route path="/feedback"  element={<Feedback />} />

        <Route path="/flight-schools"                             element={<FlightSchoolsList />} />
        <Route path="/flight-schools/add"                         element={<AddOrUpdateFlightSchool />} />
        <Route path="/flight-schools/edit/:school_id"             element={<AddOrUpdateFlightSchool />} />
        <Route path="/flight-schools/:school_id"                  element={<FlightSchoolDetail />} />
        <Route path="/flight-schools/:school_id/admin-dashboard"  element={<FlightSchoolAdminDashboard />} />

        {/* XAir staff console. Inside DashboardLayout because it is staff
            furniture, never inside SchoolPortalLayout — that layout is the
            school's own product and putting the console in it would recreate
            the confusion the console exists to remove. */}
        <Route path="/console"             element={<Console />} />
        <Route path="/console/person/:id"  element={<ConsolePerson />} />
        <Route path="/console/school/:id"  element={<ConsoleSchool />} />

        <Route path="/careers/applications"                element={<ApplicationsList />} />
        <Route path="/careers/applications/:application_id" element={<ApplicationDetail />} />
        <Route path="/careers/jobs"                         element={<JobPostingsList />} />
        <Route path="/careers/jobs/add"                     element={<AddOrUpdateJobPosting />} />
        <Route path="/careers/jobs/edit/:job_id"            element={<AddOrUpdateJobPosting />} />
      </Route>

      {/* Instructor onboarding. Its own layout, deliberately without the
          sidebar: until setup is done there is nowhere else to go, and a nav
          that bounces every click reads as broken rather than as a gate. */}
      <Route element={<InstructorOnboardingLayout />}>
        <Route path="/school/onboarding" element={<InstructorOnboarding />} />
      </Route>

      {/* The lesson itself, full screen at its own address, opened in a new
          tab from every Join button. Outside SchoolPortalLayout on purpose: a
          sidebar beside a live call is a set of ways to accidentally leave it,
          and that layout's SessionJoinPrompt would sit in the corner of the
          call inviting you to join the call you are already in. */}
      <Route path="/school/call/:bookingId" element={<SessionRoom />} />

      {/* School Admin Portal */}
      <Route element={<SchoolPortalLayout />}>
        {/* Not module-gated any more: the dashboard IS the work queue, and a
            queue of who needs you today is not an analytics feature. Its
            insights-only panels degrade server-side. */}
        <Route path="/school/dashboard" element={<SchoolDashboard />} />
        <Route path="/school/students"  element={<SchoolStudents />} />
        <Route path="/school/students/:studentId" element={<SchoolStudentDetail />} />
        <Route path="/school/staff"     element={<SchoolStaff />} />
        <Route path="/school/book-meeting" element={<SchoolBookMeeting />} />
        <Route path="/school/feedback"  element={<SchoolFeedback />} />
        {/* Split: one nav item used to hold a CFI's work surface and an
            owner's P&L. /school/tutors stays as a redirect so existing links,
            emails and bookmarks keep working. */}
        <Route path="/school/teaching"  element={<RequireModule module="tutors"><MyTeaching /></RequireModule>} />
        {/* One session, at its own address — see SessionPage.jsx. Handles both
            a 1:1 and a group class; the id tells the server which. */}
        {/* The call, without a lesson — see VideoTest.jsx. Staff-only at the
            endpoint, so this route needs no gate of its own beyond the module. */}
        <Route path="/school/teaching/video-test" element={<RequireModule module="tutors"><VideoTest /></RequireModule>} />
        <Route path="/school/sessions/:id" element={<RequireModule module="tutors"><SessionPage /></RequireModule>} />
        {/* Folded into Teaching as a collapsed section. It rendered the same
            calendar with the same column of bookings beside it — two pages for
            one week, and this was the copy without the lessons on it. */}
        <Route path="/school/teaching/setup" element={<Navigate to="/school/teaching" replace />} />
        <Route path="/school/bookings"  element={<RequireModule module="tutors"><BookingsRevenue /></RequireModule>} />
        <Route path="/school/tutors"    element={<Navigate to="/school/teaching" replace />} />
        {/* Group classes merged into Teaching. Kept as a redirect for the same
            reason /school/tutors is: the link is in emails and bookmarks. */}
        {/* Folded into Teaching. Kept as a redirect so bookmarks, the
            onboarding emails and anything still linking here land somewhere
            real rather than on a blank route. */}
        <Route path="/school/group-classes" element={<Navigate to="/school/teaching" replace />} />
        <Route path="/school/tutors/connect-return" element={<RequireModule module="tutors"><ConnectOnboardingReturn /></RequireModule>} />
        <Route path="/school/settings"  element={<RequireRole roles={["primary_admin", "staff_admin"]}><SchoolSettings /></RequireRole>} />
      </Route>

      {/* The public invitation, deliberately outside LogoOnlyLayout: that
          layout bounces anyone holding a token to /dashboard, and this page
          exists to recognise the logged-in visitor rather than to hide from
          them. /instructors/join is the canonical URL for the marketing site
          to link at; /become-an-instructor is an alias, because that is the
          shape of link people write from a website by hand. */}
      <Route path="/instructors/join" element={<BecomeInstructor />} />
      <Route path="/become-an-instructor" element={<Navigate to="/instructors/join" replace />} />

      {/* Public */}
      <Route element={<LogoOnlyLayout />}>
        <Route path="/"         element={<Authentication />} />
        <Route path="/login"    element={<Login />} />
        <Route path="/activate" element={<AdminActivation />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password"  element={<ResetPassword />} />
        {/* Independent instructor signup. Public, like /login: a CFI who has
            never had a school account creates a one-person school here. */}
        <Route path="/instructors/signup" element={<InstructorSignup />} />
      </Route>
    </Routes>
  );
}
